/**
 * useSubscriptionStatus — SSOT status reader for the subscription gate UX.
 *
 * Module-scope singleton: every caller shares the same reactive `status` ref.
 *
 * `refreshFromRows(rows)` is the PRIMARY entry point. It reads D4-enriched
 * fields (franchise_name, status, grace_until, days_remaining) already carried
 * by GET /subscriptions/my-franchises — no second HTTP call needed.
 * This fixes the silent-empty-banner bug for franqueados (the old
 * subscriptions/status/{id} endpoint requires Full access).
 *
 * Banner side-effects:
 *   any in-grace row → publish a keyed itemized banner via useGlobalBanners
 *   any setup_status not paid|exempt → publish 'setup_fee_owed' (subscribe is blocked)
 *   all rows active or empty → clear the grace banner
 *   caller is Full → NEVER publish (see below)
 *
 * NÃO PUBLICA PARA FULL (decisão do usuário, 2026-08-10): o banner é a via de
 * cobrança do FRANQUEADO — quem precisa pagar. Um Full administra a rede
 * inteira, então receberia um banner itemizado com toda franquia em carência da
 * base: ruído permanente, sem ação própria a tomar. A visão de rede do staff é
 * o console (/config/assinaturas), que existe para
 * isso. Precedente no sistema antigo: lá o banner também é restrito a papéis de
 * decisão (Franqueado/Gerente/Supervisão) — o caixa nunca vê aviso de cobrança.
 *
 * DONO ÚNICO DA REGRA DE ACESSO NO FRONT (D7, 2026-08-13). Nenhum outro
 * arquivo de `src/**` pode derivar acesso a dados de literais de status de
 * assinatura — e desde o D7 nem este deriva: ele OBEDECE `data_allowed`, que o
 * backend calcula com o mesmo par que responde 403. Ver `isInactiveRow`.
 *
 * All network lives here (no_httpservice_in_vue rule).
 * Call `_reset()` in afterEach for test isolation.
 */
import { ref } from 'vue'
import { log } from '../utils/DebugLogger'
import { useGlobalBanners } from './useGlobalBanners'
import { permissionsState } from '../services/permissionsService'
import { config } from '../services/configService'
import { isSetupOwed as sharedIsSetupOwed } from '../utils/setupFeeStatus'
import type { Subscriptions_MyFranchiseRow } from '../contracts/Subscriptions_MyFranchiseRow'

// ── Module-scope singleton state ────────────────────────────────────────────

const status = ref<string | null>(null)
const currentFranquiaId = ref<number | null>(null)

/**
 * Fase A do aviso de franquia inativa (decisão do usuário, 2026-08-12):
 * TODAS as franquias acessíveis sem acesso a dados ⇒ lock — o router
 * redireciona para a superfície de recuperação.
 * false até a primeira carga de my-franchises (fail-open: nunca trancar por
 * dado ausente) e SEMPRE false para Full.
 */
const allFranchisesInactive = ref<boolean>(false)

const BANNER_KEY = 'subscription'
const INACTIVE_BANNER_KEY = 'subscription_inactive'
const SETUP_FEE_BANNER_KEY = 'setup_fee_owed'

/**
 * Uma linha SEM acesso a dados.
 *
 * QUEM DECIDE É O BACKEND (D7, decisão "sem backfill" do usuário, 2026-08-13).
 * `data_allowed` é o veredito do MESMO par que o portão 403 consulta
 * (`SubscriptionStanding::of()` + `FranchiseAccessPolicy::allows(…, DATA)`),
 * então banner/lock e o escopo de dados não podem mais discordar. A Fase A
 * derivava isso de literais de status no navegador — uma segunda
 * implementação da linha DATA da MATRIX, que é exatamente o que o D7 mata.
 * A regra qa-py `no-billing-standing-recompute-in-frontend` impede o retorno.
 *
 * `exempt` continua curto-circuitando aqui por defesa: o backend já resolve
 * isento como STANDING_EXEMPT (DATA = true), então este ramo nunca diverge —
 * ele só garante que uma franquia isenta jamais seja trancada, nem sob o
 * fallback abaixo.
 *
 * FALLBACK TRANSITÓRIO — REMOVER quando o backend do D7 estiver em produção
 * (front e back não deployam em lockstep: um front novo pode falar com uma API
 * antiga, cuja linha não traz o campo). Enquanto isso, campo ausente/null
 * volta à derivação por status da Fase A, que é a MESMA semântica que o
 * backend passou a aplicar — as duas concordam célula a célula, então o
 * fallback não muda comportamento observável, só sobrevive à janela de deploy.
 * Ao removê-lo, `data_allowed` vira o único caminho e o `?? false` do
 * contrato (fail-closed) passa a valer sozinho.
 */
/**
 * Setup-fee obligation is owed (not paid|exempt).
 *
 * The backend owns the vocabulary (D-S8): missing row is owed, and
 * GET /subscriptions/my-franchises always sends `setup_status` with default
 * 'owed'. A missing field here is the deploy-window case (new front, old API)
 * — do not banner then, the old API does not know this concept yet.
 */
function isSetupOwed(r: Subscriptions_MyFranchiseRow): boolean {
  // THE shared predicate (src/utils/setupFeeStatus) — never a local copy.
  return sharedIsSetupOwed(r)
}

function isInactiveRow(r: Subscriptions_MyFranchiseRow): boolean {
  if (r.exempt) return false
  if (r.data_allowed == null) {
    return !(r.status === 'active' || r.status === 'grace')
  }
  return r.data_allowed === false
}

/**
 * Nomes de rota que o lock NÃO tranca — a superfície de recuperação em si
 * (para onde o lock redireciona) precisa continuar acessível. Compartilhado
 * entre o guard do router e o redirect síncrono do App.vue para não duplicar
 * a regra em 2 lugares.
 */
export const BILLING_RECOVERY_PATH = '/pagamento-e-uso'
export const LOCK_EXEMPT_ROUTE_NAMES = new Set([
  'PagamentoEUso',
  'MinhasAssinaturas',
  'SubscriptionBlocked',
])

/**
 * Quem pode se reassinar sozinho — DONO ÚNICO da regra (A13).
 *
 * É a mesma pergunta que o destino do lock faz e que o CTA "Reassinar" do
 * NoSubscriptionBanner fazia por conta própria: quem tem ação de cobrança
 * própria a tomar. Full/Franqueado/Gerente are the groups `/pagamento-e-uso`
 * is restricted to — anyone else is sent to the read-only blocked screen
 * and gets no CTA. Matches BILLING_SELF on the backend.
 */
/**
 * Billing existe na UI deste tenant? Segue a feature `pagamentoeuso`, que o
 * registro backend deriva do kill-switch AWRENV_SUBSCRIPTION_GATE. Com o gate
 * desligado NADA de cobrança aparece: sem fetch de my-franchises, sem banners
 * (setup/carência/inativa), sem lock — e os links de sidebar já somem pelo
 * próprio registro de features (decisão do usuário, 2026-09-01).
 */
export function billingUiEnabled(): boolean {
  return config.isFeatureEnabled('pagamentoeuso')
}

export function canSelfSubscribe(groups: string[] | undefined | null): boolean {
  const g = groups ?? []
  return g.includes('Full') || g.includes('Franqueado') || g.includes('Gerente')
}

/**
 * Destino do lock por grupo: quem pode se reassinar vai para a superfície de
 * cobrança; os demais vão para a tela somente-leitura de bloqueio.
 */
export function resolveLockDestination(groups: string[] | undefined | null): string {
  return canSelfSubscribe(groups) ? BILLING_RECOVERY_PATH : '/subscription/blocked'
}

/**
 * True quando o usuário autenticado é Full — o banner de cobrança não se aplica
 * a ele (ver docblock do módulo). Lido no momento da chamada, nunca cacheado:
 * permissionsState.user muda no login/refresh e o banner precisa acompanhar.
 */
function isFullCaller(): boolean {
  return permissionsState.user?.groups?.includes('Full') ?? false
}

// ── Public composable ────────────────────────────────────────────────────────

export function useSubscriptionStatus() {
  const banners = useGlobalBanners()

  /**
   * refreshFromRows — derive the subscription banner from D4-enriched
   * my-franchises rows without a second HTTP call.
   *
   * Collects ALL in-grace rows and pushes a single structured banner.
   * If all rows are active (or the list is empty), the banner is cleared.
   */
  function refreshFromRows(rows: Subscriptions_MyFranchiseRow[]): void {
    // Full não recebe banner de cobrança (ver docblock do módulo). O clear é
    // deliberado e não um early-return silencioso: se o usuário ganhou Full
    // com um banner já publicado na sessão, ele tem de sumir.
    if (isFullCaller()) {
      banners.clear(BANNER_KEY)
      banners.clear(INACTIVE_BANNER_KEY)
      banners.clear(SETUP_FEE_BANNER_KEY)
      status.value = null
      currentFranquiaId.value = null
      allFranchisesInactive.value = false
      log('useSubscriptionStatus', 'refreshFromRows', 'Caller é Full — banner de cobrança não se aplica')
      return
    }

    const owedRows = rows.filter(isSetupOwed)
    if (owedRows.length > 0) {
      banners.push({
        key: SETUP_FEE_BANNER_KEY,
        items: owedRows.map((r) => ({
          franchise_name: r.franchise_name ?? String(r.franchise_id),
          franchise_id: r.franchise_id,
          status: r.setup_status ?? 'owed',
        })),
        severity: 'warn',
      })
    } else {
      banners.clear(SETUP_FEE_BANNER_KEY)
    }

    // A franchise whose setup fee is not satisfied (not paid/exempt) must NOT show
    // any subscription banner (inactive or grace). The primary focus is the setup fee.
    const setupSatisfiedRows = rows.filter((r) => !isSetupOwed(r))

    // Fase A — franquia sem assinatura (decisão do usuário, 2026-08-12):
    // banner vermelho itemizado + derivação do lock (ver docblock do módulo).
    const inactiveRows = setupSatisfiedRows.filter(isInactiveRow)
    if (inactiveRows.length > 0) {
      banners.push({
        key: INACTIVE_BANNER_KEY,
        items: inactiveRows.map((r) => ({
          franchise_name: r.franchise_name ?? String(r.franchise_id),
          franchise_id: r.franchise_id,
          status: r.status ?? null,
          days_left: null,
        })),
        severity: 'error',
      })
    } else {
      banners.clear(INACTIVE_BANNER_KEY)
    }
    // Lock segue SÓ o veredito do backend (data_allowed — D7): com os
    // kill-switches ligados, setup owed ⇒ setupAllows=false ⇒ data_allowed=false,
    // então o lock por taxa de implantação continua. Com os gates DESLIGADOS o
    // backend libera e o front não pode trancar por conta própria — o termo
    // `isSetupOwed(r) ||` que existia aqui recomputava a regra no navegador e
    // trancava Operador/Atendente mesmo com os gates off (bug 2026-09-01).
    // O banner de setup pendente acima permanece — informativo, não lock.
    allFranchisesInactive.value = rows.length > 0 && rows.every((r) => isInactiveRow(r))

    const graceRows = setupSatisfiedRows.filter((r) => r.status === 'grace')

    if (graceRows.length === 0) {
      banners.clear(BANNER_KEY)
      // Reflect the dominant status for callers that read the status ref.
      const allActive = setupSatisfiedRows.every((r) => r.status === 'active' || r.status == null)
      status.value = allActive ? 'active' : null
      // SubscriptionBlockedPage's 'Assinar plano' CTA needs a franchise to
      // subscribe with. Até M4 esse alvo vinha das linhas 'blocked'; o corte
      // agora cancela em vez de bloquear, então o alvo é a linha 'cancelled'.
      currentFranquiaId.value =
        setupSatisfiedRows.find((r) => r.status === 'cancelled')?.franchise_id ?? null
      log('useSubscriptionStatus', 'refreshFromRows', 'Nenhuma franquia em carência — banner removido')
      return
    }


    status.value = 'grace'
    currentFranquiaId.value = graceRows[0].franchise_id

    const items = graceRows.map((r) => ({
      franchise_name: r.franchise_name ?? String(r.franchise_id),
      days_left: r.days_remaining ?? null,
      franchise_id: r.franchise_id,
      status: r.status ?? 'grace',
    }))

    banners.push({
      key: BANNER_KEY,
      items,
      severity: 'warn',
    })

    log('useSubscriptionStatus', 'refreshFromRows', 'Banner de assinatura publicado', {
      count: items.length,
      franchises: items.map((i) => i.franchise_name),
    })
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    status.value = null
    currentFranquiaId.value = null
    allFranchisesInactive.value = false
    banners.clear(BANNER_KEY)
    banners.clear(INACTIVE_BANNER_KEY)
    banners.clear(SETUP_FEE_BANNER_KEY)
  }

  return {
    status,
    currentFranquiaId,
    allFranchisesInactive,
    refreshFromRows,
    _reset,
  }
}
